/**
 * Pure helpers for the normalization wizard's 2D plan view. No IO, no
 * randomness — deterministic for identical inputs (sorted outputs).
 */
import type { ApiSpace } from "../api/types";
import { flipYPoints } from "../util/coordinate";
import type { ModelSummary, PlanPoint } from "../viewer/ifc/types";

/** A space joined with the best available footprint polygon. */
export interface PlanSpace {
  space: ApiSpace;
  footprint: PlanPoint[] | null;
}

/** One storey tab in the wizard. */
export interface StoreyTab {
  /** IFC express storey id, or null bucket for unassigned spaces. */
  storeyId: number | null;
  name: string;
  spaceCount: number;
}

/** Effective occupancy: engineer override wins over the classification. */
export function effectiveOccupancy(space: ApiSpace): string | null {
  return space.occupancy_override ?? space.occupancy;
}

/**
 * Color per occupancy kind for the plan fill + legend. Keyed by the canonical
 * occupancy wire string (snake_case). `Partial<>` intentionally — when the
 * Rust enum grows a 20th category, the wizard renders it with the unclassified
 * fallback color until a palette entry is added. See ADR 0010.
 */
export const OCCUPANCY_COLORS: Partial<Record<string, string>> = {
  office: "#4a90d9",
  conference: "#7e6bd9",
  corridor: "#9aa3b2",
  kitchen: "#e0823c",
  patient_room: "#3cb8a8",
  operating_room: "#2e8b57",
  classroom: "#c9a227",
  data_hall: "#5560e0",
  electrical_room: "#e0c23c",
  mechanical_room: "#b07040",
  storage: "#8a7f72",
  lobby: "#d96ba8",
  restroom: "#56b4d3",
  // Newly-supported categories (ENG-14669):
  break_room: "#d9a86b",
  exam_treatment: "#5ab69a",
  assembly: "#bb6bd9",
  sleeping: "#5e7eb2",
  stairwell: "#7c8a99",
  other: "#777788",
};

const UNCLASSIFIED_COLOR = "#3a3a52";

export function occupancyColor(occupancy: string | null): string {
  if (occupancy === null) return UNCLASSIFIED_COLOR;
  return OCCUPANCY_COLORS[occupancy] ?? UNCLASSIFIED_COLOR;
}

/**
 * Join API space rows with footprints, preferring the server-provided polygon
 * and falling back to the locally parsed `ModelSummary` matched by IFC
 * express id.
 */
export function joinFootprints(
  spaces: readonly ApiSpace[],
  summary: ModelSummary | null,
): PlanSpace[] {
  const byIfcId = new Map<number, PlanPoint[]>();
  if (summary) {
    for (const s of summary.spaces) {
      if (s.footprint) byIfcId.set(s.id, [...s.footprint]);
    }
  }
  return spaces.map((space) => ({
    space,
    footprint:
      space.footprint ?? (space.ifc_id !== null ? (byIfcId.get(space.ifc_id) ?? null) : null),
  }));
}

/**
 * Derive the storey tabs from the space rows, sorted by storey id (the
 * extractor orders storeys by elevation, then express id); the unassigned
 * bucket, if any, comes last.
 */
export function storeyTabs(spaces: readonly ApiSpace[]): StoreyTab[] {
  const byId = new Map<number | null, StoreyTab>();
  for (const space of spaces) {
    const existing = byId.get(space.storey_ifc_id);
    if (existing) {
      existing.spaceCount += 1;
    } else {
      byId.set(space.storey_ifc_id, {
        storeyId: space.storey_ifc_id,
        name:
          space.storey_ifc_id === null
            ? "No storey"
            : (space.storey_name ?? `Storey ${space.storey_ifc_id}`),
        spaceCount: 1,
      });
    }
  }
  return [...byId.values()].sort((a, b) => {
    if (a.storeyId === null) return 1;
    if (b.storeyId === null) return -1;
    return a.storeyId - b.storeyId;
  });
}

/**
 * Storeys that a viewer/selector should list: only those that own at least one
 * space. Spaceless datum levels (e.g. "TOF Footing", a foundation reference
 * plane with geometry but no `IfcSpace`) are dropped so they never appear as a
 * selectable storey. The raw `storeys` array is left intact — other code needs
 * its elevations; this filters ONLY at the selector layer.
 *
 * `getId` reads the storey's IFC express id; `getStoreyId` reads the express id
 * of a space's containing storey (null for unassigned). Deterministic — order
 * of the input `storeys` is preserved.
 */
export function storeysWithSpaces<S, K>(
  storeys: readonly S[],
  spaces: readonly K[],
  getId: (storey: S) => number,
  getStoreyId: (space: K) => number | null,
): S[] {
  const occupied = new Set<number>();
  for (const space of spaces) {
    const id = getStoreyId(space);
    if (id !== null) occupied.add(id);
  }
  return storeys.filter((storey) => occupied.has(getId(storey)));
}

export interface PlanBounds {
  minX: number;
  minY: number;
  width: number;
  height: number;
}

/** Bounding box of all footprints, with a margin (meters). Null if none. */
export function planBounds(spaces: readonly PlanSpace[], margin = 1): PlanBounds | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const { footprint } of spaces) {
    if (!footprint) continue;
    for (const [x, y] of footprint) {
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  if (minX === Infinity) return null;
  return {
    minX: minX - margin,
    minY: minY - margin,
    width: maxX - minX + 2 * margin,
    height: maxY - minY + 2 * margin,
  };
}

/**
 * SVG `points` attribute for a footprint, with the y axis flipped so plan
 * north stays up (SVG y grows downward).
 */
export function svgPoints(footprint: readonly PlanPoint[]): string {
  return flipYPoints(footprint);
}

/** Polygon centroid (vertex average — fine for labels), y flipped for SVG. */
export function svgCentroid(footprint: readonly PlanPoint[]): [number, number] {
  let sx = 0;
  let sy = 0;
  for (const [x, y] of footprint) {
    sx += x;
    sy += y;
  }
  const n = footprint.length || 1;
  return [sx / n, -sy / n];
}
